<template>
  <Modal
    v-model:visible="isVisible"
    class="clone-order-modal"
    :header="
      t('common.duplicateParam', {
        param: t('orders.orders', numOrdersSelected),
      })
    "
    @keypress.enter="onConfirm"
    @keypress.esc="isVisible = false"
  >
    <p>
      {{
        t('shipments.confirmDuplicationsCountOrders', {
          count: numOrdersSelected,
        })
      }}
    </p>

    <div class="flex flex-column align-items-start">
      <InputWrapper
        v-if="showCloneReferenceNumbersOption"
        v-slot="{ inputId }"
        class="checkbox-wrapper"
        :input-id="`${uid}-clone-reference-numbers`"
        :label="t('orders.cloneReferenceNumbers')"
        inline
      >
        <Checkbox v-model="cloneReferenceNumbers" :input-id="inputId" binary />
      </InputWrapper>

      <InputWrapper
        v-if="showCloneTagsOption"
        v-slot="{ inputId }"
        class="checkbox-wrapper"
        :input-id="`${uid}-clone-tags`"
        :label="t('orders.cloneTags')"
        inline
      >
        <Checkbox v-model="cloneTags" :input-id="inputId" binary />
      </InputWrapper>

      <InputWrapper
        v-slot="{ inputId }"
        class="checkbox-wrapper"
        :input-id="`${uid}-skip-auto-actions`"
        :label="
          t(
            'common.skipAutoActionParam',
            {
              param: t('orders.orders', numOrdersSelected),
            },
            numOrdersSelected,
          )
        "
        inline
      >
        <Checkbox v-model="skipAutoActions" :input-id="inputId" binary />
      </InputWrapper>
    </div>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="isVisible = false"
      />

      <RygenButton
        :label="
          t('common.duplicateParam', {
            param: t('orders.orders', numOrdersSelected),
          })
        "
        icon="pi pi-copy"
        @click="onConfirm"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useUid } from '@rygen/rygen-ui/composables';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import Checkbox from 'primevue/checkbox';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const cloneReferenceNumbers = defineModel('cloneReferenceNumbers', {
  type: Boolean,
  default: () => false,
});

const cloneTags = defineModel('cloneTags', {
  type: Boolean,
  default: () => false,
});

const skipAutoActions = defineModel('skipAutoActions', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  numOrdersSelected: {
    type: Number,
    default: () => 1,
  },
  showCloneTagsOption: {
    type: Boolean,
    default: () => false,
  },
  showCloneReferenceNumbersOption: {
    type: Boolean,
    default: () => false,
  },
});

const {
  numOrdersSelected,
  showCloneReferenceNumbersOption,
  showCloneTagsOption,
} = toRefs(props);
const emit = defineEmits<{
  (e: 'confirm'): void;
}>();

const { t } = useI18n();

const { uid } = useUid('order-clone-modal');

const onConfirm = () => {
  emit('confirm');
  isVisible.value = false;
};

watch(isVisible, (newVal: boolean) => {
  if (!newVal) {
    cloneReferenceNumbers.value = false;
    cloneTags.value = false;
    skipAutoActions.value = false;
  }
});
</script>

<style>
.clone-order-modal {
  p {
    margin-bottom: var(--sp-3);
  }

  .checkbox-wrapper {
    flex-direction: row-reverse;
    align-items: center;
  }
}
</style>
